import { Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

interface AttachedFile {
  id: number;
  file: string;
}

interface FileAttachmentsListProps {
  attachedFiles: AttachedFile[];
  newFiles: File[];
  themeColor: string;
  onDeleteFile: (fileId: number) => void;
  onRemoveNewFile: (index: number) => void;
}

const getFileName = (url: string) => {
  try {
    const cleanUrl = url.split("?")[0];
    return decodeURIComponent(cleanUrl.split("/").pop() || "Unnamed file");
  } catch {
    return "Unnamed file";
  }
};

export const FileAttachmentsList: React.FC<FileAttachmentsListProps> = ({
  attachedFiles,
  newFiles,
  themeColor,
  onDeleteFile,
  onRemoveNewFile,
}) => {
  const { t } = useTranslation();

  if (attachedFiles.length === 0 && newFiles.length === 0) {
    return null;
  }

  return (
    <ul className="mt-2 space-y-2">
      {attachedFiles.map((file, idx) => (
        <li
          key={`attached-${idx}`}
          className="flex items-center justify-between bg-gray-50 p-2 rounded border"
        >
          <div className="flex items-center gap-1">
            <span className="truncate max-w-[80%]" title={file.file}>
              {getFileName(file.file)}
            </span>
            <a
              href={file.file}
              target="_blank"
              rel="noopener noreferrer"
              download
              title={t("taskHub.modal.downloadFile") || ""}
              className="text-blue-500 hover:text-blue-700"
            >
              <DownloadOutlined className="text-xl" />
            </a>
          </div>

          <Button
            style={{ backgroundColor: themeColor }}
            danger
            type="text"
            size="small"
            onClick={() => onDeleteFile(file.id)}
          >
            <span className="!p-0">{t("taskHub.modal.delete") || ""}</span>
          </Button>
        </li>
      ))}
      {newFiles.map((file, idx) => (
        <li
          key={`new-${idx}`}
          className="flex items-center justify-between bg-blue-50 p-2 rounded border border-blue-200"
        >
          <div className="flex items-center gap-1">
            <span className="truncate max-w-[80%]" title={file.name}>
              {file.name}
            </span>
          </div>

          <Button
            style={{ backgroundColor: themeColor }}
            danger
            type="text"
            size="small"
            onClick={() => onRemoveNewFile(idx)}
          >
            <span className="!p-0">{t("taskHub.modal.delete") || ""}</span>
          </Button>
        </li>
      ))}
    </ul>
  );
};
